import {
  RailRowMenu,
  railRowMenuProps,
} from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailRowMenu";
import type { RailRowMenuController } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/useRailRowContextMenu";
import { isNativePlatform } from "@/native/platform";
import { ObjectActionMenu } from "@/object-actions/renderers/ObjectActionMenu";
import { ObjectActionMenuCloseProvider } from "@/object-actions/renderers/ObjectActionMenuCloseContext";
import type { ObjectKind, ResolvedObjectAction } from "@/object-actions/types";
import { cn } from "@/utils";

export interface ObjectActionCardMenuProps {
  /** Shared controller — drives both the kebab and the card's right-click. */
  controller: RailRowMenuController;
  actions: ResolvedObjectAction[];
  kind: ObjectKind;
  /** Resource name, used to label the kebab for assistive tech. */
  name?: string;
}

/**
 * Registry-backed "more" menu for workspace grid cards. Reuses the rail's
 * cursor-anchored {@link RailRowMenu} so a kebab click drops the menu below the
 * button while a right-click anywhere on the card opens it at the pointer — both
 * driven by the one {@link RailRowMenuController} the card also spreads onto its
 * root via `onContextMenu`. The kebab hides until the card is hovered (or the
 * menu is open), matching the hover-reveal of the previous hard-coded menus.
 */
export function ObjectActionCardMenu({
  controller,
  actions,
  kind,
  name,
}: ObjectActionCardMenuProps) {
  return (
    <RailRowMenu
      {...railRowMenuProps(controller)}
      label={name ?? ""}
      kebabWrapperClassName={cn(
        "transition-opacity duration-150",
        // Native has no hover to reveal the kebab — keep it visible.
        controller.open
          ? "opacity-100"
          : cn(
              "opacity-0 group-hover:opacity-100",
              isNativePlatform() && "opacity-100",
            ),
      )}
    >
      {/* Destination submenus commit from SubTrigger clicks, which Radix
          treats as "open", not "select" — the provider lets them dismiss
          the whole menu. */}
      <ObjectActionMenuCloseProvider value={() => controller.setOpen(false)}>
        <ObjectActionMenu actions={actions} kind={kind} name={name} />
      </ObjectActionMenuCloseProvider>
    </RailRowMenu>
  );
}
